// Unified playback bridge — abstracts Connector (Tauri), Baton (iOS), and local playback.
// MiniPlayer and all UI code calls ONLY these functions — never connector.ts or baton directly.
//
// iOS PARITY: iOS is always a REMOTE client. It never scans or serves local files.
// All local library playback on iOS flows through BATON WebSocket signaling:
//   1. requestRemoteFile() → BATON requestLocalFile command → home Connector
//   2. Home Connector registers file with its proxy, replies with tunnel URL
//   3. iOS receives tunnel URL via handleRemoteFileEvent()
//   4. iOS plays the tunnel URL via AVPlayer (playUri path) — no Tauri IPC involved

import * as Connector from './connector';
import { useMaestroStore } from '../store/maestroStore';
import { useNowPlayingStore } from '../store/nowPlayingStore';
import api from '../services/api';
import { createCommand, createEvent } from '../protocols/BATON';
import type { RequestLocalFileCommand, LocalFileReadyEvent, LocalFileErrorEvent } from '../protocols/BATON';
import { isHomeLibraryHost } from '../services/localLibraryIpcGate';

function getLocalMachineId(): string | null {
  try {
    return localStorage.getItem('maestro_machine_id');
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// URL Cache — avoids repeated round-trips for files already resolved.
// Tunnel/proxy URLs are valid for 5 minutes; we cache for 4.
// ---------------------------------------------------------------------------

const localFileUrlCache = new Map<string, { url: string; expiresAt: number }>();
const CACHE_TTL_MS = 4 * 60 * 1000;

/**
 * Detect and repair double-wrapped /stream/ URLs produced by a contaminated
 * tunnel base URL.  A valid stream URL has exactly ONE /stream/{session}/{hmac}/{exp}
 * segment.  If a second /stream/ segment is present, strip everything before the
 * last /stream/ so only the freshest session survives.
 */
function sanitizeStreamUrl(url: string): string {
  try {
    const u = new URL(url);
    const last = u.pathname.lastIndexOf('/stream/');
    if (last > 0) {
      u.pathname = u.pathname.slice(last);
      const repaired = u.href;
      if (repaired !== url) {
        console.warn('[PlaybackBridge] Repaired double-wrapped URL');
      }
      return repaired;
    }
    return url;
  } catch {
    const marker = '/stream/';
    const last = url.lastIndexOf(marker);
    if (last <= 0) return url;
    const first = url.indexOf('://');
    const hostEnd = url.indexOf('/', first >= 0 ? first + 3 : 0);
    if (hostEnd < 0 || last <= hostEnd) return url;
    console.warn('[PlaybackBridge] Repaired double-wrapped URL');
    return url.slice(0, hostEnd) + url.slice(last);
  }
}

export function getCachedFileUrl(filePath: string): string | null {
  const entry = localFileUrlCache.get(filePath);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    localFileUrlCache.delete(filePath);
    return null;
  }
  return sanitizeStreamUrl(entry.url);
}

function cacheFileUrl(filePath: string, url: string): void {
  localFileUrlCache.set(filePath, { url: sanitizeStreamUrl(url), expiresAt: Date.now() + CACHE_TTL_MS });
}

/**
 * Evict a cached URL by stream URL (called when audio playback gets 403/error).
 * Also accepts a file_path to evict directly.
 */
export function invalidateCachedUrl(streamUrlOrPath: string): void {
  // Direct path match
  if (localFileUrlCache.has(streamUrlOrPath)) {
    localFileUrlCache.delete(streamUrlOrPath);
    return;
  }
  // Scan for entries matching the stream URL
  for (const [path, entry] of localFileUrlCache) {
    if (entry.url === streamUrlOrPath) {
      localFileUrlCache.delete(path);
      return;
    }
  }
}

export function clearAllCachedFileUrls(): void {
  localFileUrlCache.clear();
}

export type BridgeMode = 'local' | 'connector' | 'ios-native' | 'baton';

export interface PlayUriParams {
  rendererId: string;
  uri: string;
  title: string;
  artist: string;
  album: string;
  formatId: number;
  durationSecs: number;
  artUrl?: string;
  startSeconds?: number;
}

export interface PlayLocalFileParams {
  filePath: string;
  rendererId: string;
  title: string;
  artist: string;
  album: string;
  durationSecs: number;
  artworkId?: string | null;
}

export interface PlaybackBridge {
  mode: BridgeMode;

  playUri(params: PlayUriParams): Promise<void>;
  playLocalFile?(params: PlayLocalFileParams): Promise<string>;
  play(rendererId: string): Promise<void>;
  pause(rendererId: string): Promise<void>;
  stop(rendererId: string): Promise<void>;
  seek(rendererId: string, seconds: number): Promise<void>;
  setVolume(rendererId: string, volume: number): Promise<void>;
}

export function getBridgeMode(): BridgeMode {
  if (Connector.isConnectorAvailable()) return 'connector';
  if (typeof window !== 'undefined' && (window as any).__maestroNative?.isNativeApp) return 'ios-native';
  if (typeof window !== 'undefined' && 'maestroBridge' in window) return 'baton';
  return 'local';
}

export function isBridgeAvailable(): boolean {
  return getBridgeMode() !== 'local';
}

export function isNativeBridgeMode(): boolean {
  const m = getBridgeMode();
  return m === 'connector' || m === 'ios-native';
}

export function createBridge(): PlaybackBridge | null {
  const mode = getBridgeMode();

  if (mode === 'connector') {
    return {
      mode: 'connector',
      async playUri(p) {
        const store = useMaestroStore.getState();
        const activeId = store.discovery?.activeZone?.id || store.activeConnectorRenderer?.id || null;
        if (activeId && p.rendererId !== activeId) {
          console.warn(`[Bridge] playUri aborted — renderer mismatch: called for ${p.rendererId}, active is ${activeId}`);
          return;
        }
        console.log('[Bridge] forwarding playUri to Connector with format_id:', p.formatId, 'startSeconds:', p.startSeconds ?? 'none');
        await Connector.playUri(p.rendererId, p.uri, p.title, p.artist, p.album, p.formatId, p.durationSecs, p.artUrl, p.startSeconds);
      },
      // iOS PARITY: Local file playback routes through Connector's HTTP proxy.
      // iOS will use the same proxy URL obtained via BATON signaling.
      async playLocalFile(p) {
        return Connector.playLocalFile(
          p.filePath,
          p.rendererId,
          p.title,
          p.artist,
          p.album,
          p.durationSecs,
          p.artworkId ?? null,
        );
      },
      async play(id) { await Connector.play(id); },
      async pause(id) { await Connector.pause(id); },
      async stop(id) { await Connector.stop(id); },
      async seek(id, secs) { await Connector.seek(id, secs); },
      async setVolume(id, vol) { await Connector.setVolume(id, vol); },
    };
  }

  if (mode === 'ios-native') {
    const native = (window as any).__maestroNative;
    const np = useNowPlayingStore.getState;
    return {
      mode: 'ios-native',
      async playUri(p) {
        console.log('[Bridge:iOS] playUri →', p.rendererId, p.title, 'format:', p.formatId);
        native?.postMessage('playUri', {
          rendererId: p.rendererId,
          uri: p.uri,
          title: p.title,
          artist: p.artist,
          album: p.album,
          formatId: p.formatId,
          durationSecs: p.durationSecs,
          artUrl: p.artUrl || '',
          startSeconds: p.startSeconds,
        });
        np().setStatus('playing');
      },
      async play(id) {
        console.log('[Bridge:iOS] play →', id);
        native?.postMessage('sendCommand', { deviceId: id, command: 'play' });
        np().setStatus('playing');
      },
      async pause(id) {
        console.log('[Bridge:iOS] pause →', id);
        native?.postMessage('sendCommand', { deviceId: id, command: 'pause' });
        np().setStatus('paused');
      },
      async stop(id) {
        console.log('[Bridge:iOS] stop →', id);
        native?.postMessage('sendCommand', { deviceId: id, command: 'stop' });
        np().setStatus('idle');
      },
      async seek(id, secs) {
        console.log('[Bridge:iOS] seek →', id, Math.floor(secs));
        native?.postMessage('seek', { deviceId: id, seconds: Math.floor(secs) });
      },
      async setVolume(id, vol) {
        console.log('[Bridge:iOS] setVolume →', id, Math.round(vol));
        native?.postMessage('setVolume', { deviceId: id, level: Math.round(vol) });
      },
    };
  }

  if (mode === 'baton') {
    const send = (type: string, payload: object) =>
      (window as any).maestroBridge?.send({ type, ...payload });

    return {
      mode: 'baton',
      async playUri(p) { send('playUri', p); },
      async play(id) { send('play', { rendererId: id }); },
      async pause(id) { send('pause', { rendererId: id }); },
      async stop(id) { send('stop', { rendererId: id }); },
      async seek(id, secs) { send('seek', { rendererId: id, seconds: secs }); },
      async setVolume(id, vol) { send('setVolume', { rendererId: id, volume: vol }); },
    };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Remote Library Access
// ---------------------------------------------------------------------------

type PendingFileRequest = {
  resolve: (url: string) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

const pendingFileRequests = new Map<string, PendingFileRequest>();

// ---------------------------------------------------------------------------
// Fire-and-forget dispatch pattern (non-blocking)
// ---------------------------------------------------------------------------

type PendingLocalDispatch = {
  trackId: string;
  track: any;
  timer: ReturnType<typeof setTimeout>;
};

const pendingLocalDispatches = new Map<string, PendingLocalDispatch>();

/**
 * Fire-and-forget: resolve a local file path to a playable URL and start playback.
 *
 * Resolution order (fastest first):
 *   1. URL cache → instant (~0ms)
 *   2. Connector IPC (when Tauri present + file is local) → ~50ms
 *   3. Direct tunnel fetch (remote peer with known tunnel URL) → ~200ms
 *   4. BATON WebSocket relay (fallback for remote files) → ~2-8s
 *
 * After successful resolution, caches the URL and prefetches the next
 * few queue tracks in the background so track advance is instant.
 *
 * iOS PARITY: iOS has no Connector, so it takes path #3 or #4.
 */
export function dispatchLocalFileRequest(
  filePath: string,
  trackId: string,
  track: any,
  ws: WebSocket | null,
  machineId?: string,
): void {
  const store = useMaestroStore.getState();

  // ── 1. Cache hit → instant playback ──
  const cached = getCachedFileUrl(filePath);
  if (cached) {
    console.log('[LocalFile] Cache HIT:', filePath.split('/').pop());
    import('../handlers/ClientTransportHandler').then(({ getClientTransport }) => {
      const s = useMaestroStore.getState();
      if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
        getClientTransport().playLocalStream({ ...track, streamUrl: cached });
      }
    });
    prefetchNextLocalTracks(ws, machineId);
    return;
  }

  // ── 2. Connector IPC — home library host only (never on remote Connector shells).
  // iOS PARITY: iOS never enters this branch — isConnectorAvailable() is false.
  if (Connector.isConnectorAvailable() && isHomeLibraryHost()) {
    const localId = getLocalMachineId();
    const isLocalFile = !machineId || !localId || machineId === localId;
    if (isLocalFile) {
      console.log('[LocalFile] Connector IPC path:', filePath.split('/').pop());
      Connector.registerFileUrl(filePath).then((url) => {
        cacheFileUrl(filePath, url);
        const s = useMaestroStore.getState();
        if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
          import('../handlers/ClientTransportHandler').then(({ getClientTransport }) => {
            getClientTransport().playLocalStream({ ...track, streamUrl: url });
          });
        }
        prefetchNextLocalTracks(ws, machineId);
      }).catch((err) => {
        const errMsg = typeof err === 'string' ? err : err?.message || '';
        const isFileNotFound = errMsg.includes('not found') || errMsg.includes('File not found');
        console.warn('[LocalFile] Connector IPC failed:', errMsg);

        if (isFileNotFound) {
          // File doesn't exist on this machine — try the tunnel API to find
          // which machine has it, then fetch directly from that tunnel.
          tryTunnelFallback(filePath, trackId, track, ws, machineId);
        } else if (ws && ws.readyState === WebSocket.OPEN) {
          sendBatonFileRequest(filePath, trackId, track, ws, machineId);
        } else {
          console.error('[LocalFile] No fallback path — BATON not connected');
          const s = useMaestroStore.getState();
          if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
            s.setPlaybackState('stopped');
          }
        }
      });
      return;
    }
  }

  // ── 3. Direct tunnel fetch (remote peer with known tunnel URL) ──
  const tunnelUrl = track?._source_tunnel_url;
  if (tunnelUrl && machineId) {
    console.log('[LocalFile] Direct tunnel path:', filePath.split('/').pop());
    fetchFromTunnel(tunnelUrl, filePath, machineId).then((url) => {
      cacheFileUrl(filePath, url);
      const s = useMaestroStore.getState();
      if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
        import('../handlers/ClientTransportHandler').then(({ getClientTransport }) => {
          getClientTransport().playLocalStream({ ...track, streamUrl: url });
        });
      }
      prefetchNextLocalTracks(ws, machineId);
    }).catch((err) => {
      console.warn('[LocalFile] Direct tunnel failed, falling back to BATON:', err?.message || err);
      if (ws && ws.readyState === WebSocket.OPEN) {
        sendBatonFileRequest(filePath, trackId, track, ws, machineId);
      } else {
        store.setPlaybackState('stopped');
      }
    });
    return;
  }

  // ── 4. BATON WebSocket relay (remote files or no Connector) ──
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    console.error('[LocalFile] No BATON connection — cannot request local file');
    store.setPlaybackState('stopped');
    return;
  }

  sendBatonFileRequest(filePath, trackId, track, ws, machineId);
}

/**
 * When a local file isn't found by the Connector, query Mycelium for ALL
 * registered tunnel URLs and try each one until we find the file. This handles
 * the case where the NAS/library is mounted on a different machine.
 */
async function tryTunnelFallback(
  filePath: string,
  trackId: string,
  track: any,
  ws: WebSocket | null,
  machineId?: string,
): Promise<void> {
  try {
    // Route through Mycelium server-side to avoid CORS — Mycelium tries all
    // registered tunnels and returns the first successful stream URL.
    console.log('[LocalFile] Resolving via Mycelium server-side proxy');
    const data = await api.post('/api/library/resolve-file', {
      file_path: filePath,
      ...(machineId ? { machine_id: machineId } : {}),
    });

    if (data?.url) {
      cacheFileUrl(filePath, data.url);
      console.log('[LocalFile] Server-side resolve succeeded via', data.machine_id);
      const s = useMaestroStore.getState();
      if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
        const { getClientTransport } = await import('../handlers/ClientTransportHandler');
        getClientTransport().playLocalStream({ ...track, streamUrl: data.url });
      }
      prefetchNextLocalTracks(ws, machineId);
      return;
    }
  } catch (err: any) {
    console.warn('[LocalFile] Server-side resolve failed:', err?.message || err);
  }

  // Final fallback: BATON relay
  if (ws && ws.readyState === WebSocket.OPEN) {
    sendBatonFileRequest(filePath, trackId, track, ws, machineId);
  } else {
    const s = useMaestroStore.getState();
    if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
      s.setPlaybackState('stopped');
    }
  }
}

function sendBatonFileRequest(
  filePath: string,
  trackId: string,
  track: any,
  ws: WebSocket,
  machineId?: string,
): void {
  const requestId = `rlf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const userId = (typeof localStorage !== 'undefined'
    ? localStorage.getItem('maestro_user_id')
    : null) || 'default';

  const timer = setTimeout(() => {
    pendingLocalDispatches.delete(requestId);
    console.error('[LocalFile] Timeout: home library did not respond within 10 seconds');
    const s = useMaestroStore.getState();
    if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
      s.setPlaybackState('stopped');
    }
  }, 10_000);

  pendingLocalDispatches.set(requestId, { trackId, track, timer });

  const msg = createCommand('library', {
    cmd: 'requestLocalFile',
    requestId,
    filePath,
    userId,
    machineId,
    trackId,
  } as RequestLocalFileCommand);
  ws.send(JSON.stringify(msg));
  console.log('[LocalFile] BATON request sent:', filePath.split('/').pop());
}

/**
 * Directly call a remote peer's tunnel to register a file and get a signed stream URL.
 * This bypasses the BATON relay for faster resolution (~200ms vs 2-8s).
 */
async function fetchFromTunnel(
  tunnelUrl: string,
  filePath: string,
  machineId: string,
): Promise<string> {
  const resp = await fetch(`${tunnelUrl}/api/register-file`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_path: filePath, machine_id: machineId }),
  });
  if (!resp.ok) throw new Error(`Tunnel register-file ${resp.status}`);
  const data = await resp.json();
  if (!data?.url) throw new Error('No URL in tunnel response');
  return data.url;
}

// ---------------------------------------------------------------------------
// Prefetch — resolve URLs for upcoming queue tracks in the background
// ---------------------------------------------------------------------------

let prefetchInProgress = false;

export function prefetchNextLocalTracks(ws: WebSocket | null, machineId?: string): void {
  if (prefetchInProgress) return;
  prefetchInProgress = true;
  setTimeout(() => { prefetchInProgress = false; }, 2000);

  try {
    const store = useMaestroStore.getState();
    const { queue, queueIndex } = store;
    const upcoming = queue.slice(queueIndex + 1, queueIndex + 4);

    for (const item of upcoming) {
      const t = item?.track;
      if (!t?.file_path || t?.source !== 'local_library') continue;
      if (getCachedFileUrl(t.file_path)) continue;

      if (Connector.isConnectorAvailable() && isHomeLibraryHost()) {
        const localId = getLocalMachineId();
        const isLocal = !t.machine_id || !localId || t.machine_id === localId;
        if (isLocal) {
          Connector.registerFileUrl(t.file_path)
            .then(url => { cacheFileUrl(t.file_path, url); console.log('[Prefetch] cached:', t.title); })
            .catch(() => {});
          continue;
        }
      }

      // Remote viewer: prefetch via Mycelium (server-side tunnel resolve).
      if (!isHomeLibraryHost() && t.file_path) {
        api.post('/api/library/resolve-file', {
          file_path: t.file_path,
          ...(t.machine_id ? { machine_id: t.machine_id } : {}),
        })
          .then((data) => {
            if (data?.url) {
              cacheFileUrl(t.file_path, data.url);
              console.log('[Prefetch] Mycelium cached:', t.title);
            }
          })
          .catch(() => {});
        continue;
      }

      if (t._source_tunnel_url && t.machine_id) {
        fetchFromTunnel(t._source_tunnel_url, t.file_path, t.machine_id)
          .then(url => { cacheFileUrl(t.file_path, url); console.log('[Prefetch] tunnel cached:', t.title); })
          .catch(() => {});
        continue;
      }

      if (ws && ws.readyState === WebSocket.OPEN) {
        const userId = (typeof localStorage !== 'undefined' ? localStorage.getItem('maestro_user_id') : null) || 'default';
        requestRemoteFile(t.file_path, userId, ws, t.machine_id)
          .then(url => { cacheFileUrl(t.file_path, url); console.log('[Prefetch] cached:', t.title); })
          .catch(() => {});
      }
    }
  } catch { /* best effort */ }
}

/**
 * Returns true if the id refers to a local library entity (stable id from Connector SQLite).
 */
export function isLocalTrackId(id: unknown): boolean {
  const s = String(id || '');
  return s.startsWith('local:track:') || s.startsWith('local:album:');
}

/**
 * Returns true if the track should use local file playback (not Qobuz stream resolution).
 */
export function isLocalLibraryTrack(track: any): boolean {
  if (!track) return false;
  if (track.source === 'local_library') return true;
  const id = String(track.id || track.trackId || track.serviceTrackId || '');
  if (isLocalTrackId(id)) return true;
  if (track.file_path && !track.qobuzTrackId && !track.serviceTrackId) return true;
  return false;
}

/**
 * Resolve a local library file_path to a playable HTTP URL.
 *
 * Platform routing:
 * - Connector (Tauri desktop): registers the file with the local proxy via IPC,
 *   returns a signed LAN/tunnel URL immediately.
 * - iOS / web (no Tauri): sends a BATON requestLocalFile command over WebSocket
 *   to the home Connector, which replies with a tunnel URL. Requires an active
 *   BATON connection.
 *
 * iOS PARITY: iOS uses this function exclusively for local library playback.
 * The returned URL points to the home Connector's Cloudflare Tunnel — iOS plays
 * it directly via AVPlayer. No Tauri IPC is ever involved on iOS.
 */
export async function resolveLocalFileUrl(
  filePath: string,
  ws: WebSocket | null,
  machineId?: string,
): Promise<string> {
  const cached = getCachedFileUrl(filePath);
  if (cached) return cached;

  // Path 1: Connector IPC — home library host only.
  if (Connector.isConnectorAvailable() && isHomeLibraryHost()) {
    const localId = getLocalMachineId();
    const isLocalFile = !machineId || !localId || machineId === localId;
    if (isLocalFile) {
      try {
        const url = await Connector.registerFileUrl(filePath);
        cacheFileUrl(filePath, url);
        return url;
      } catch (err) {
        console.warn('[LocalFile] registerFileUrl failed on home host — trying Mycelium:', err);
      }
    } else {
      console.log(`[LocalFile] machineId ${machineId} !== local ${localId} — routing via Mycelium`);
    }
  } else if (Connector.isConnectorAvailable() && !isHomeLibraryHost()) {
    console.log('[LocalFile] Remote viewer — resolving via Mycelium tunnel');
  }

  // Path 2a: Server-side resolve via Mycelium (browser remote — no BATON required).
  const tryMyceliumResolve = async (mid?: string) => {
    const data = await api.post('/api/library/resolve-file', {
      file_path: filePath,
      ...(mid ? { machine_id: mid } : {}),
    });
    if (data?.url) {
      cacheFileUrl(filePath, data.url);
      console.log('[LocalFile] Mycelium resolve-file OK via', data.machine_id || mid || 'any');
      return data.url as string;
    }
    return null;
  };

  try {
    const url = await tryMyceliumResolve(machineId);
    if (url) return url;
  } catch (err: any) {
    console.warn('[LocalFile] Mycelium resolve-file failed:', err?.message || err);
  }

  if (machineId) {
    try {
      const url = await tryMyceliumResolve(undefined);
      if (url) return url;
    } catch (err: any) {
      console.warn('[LocalFile] Mycelium resolve-file (all machines) failed:', err?.message || err);
    }
  }

  // Path 2b: BATON WebSocket relay (iOS / remote with active session).
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    throw new Error('No BATON connection and file is not local');
  }
  const userId = (typeof localStorage !== 'undefined'
    ? localStorage.getItem('maestro_user_id')
    : null) || 'default';
  const url = await requestRemoteFile(filePath, userId, ws, machineId);
  cacheFileUrl(filePath, url);
  return url;
}

/**
 * Called by a REMOTE client to request a file from the home Connector.
 * Sends a requestLocalFile BATON command and waits for a localFileReady event.
 *
 * iOS PARITY: This is the primary path for iOS local library playback.
 * Uses standard WebSocket — no Tauri IPC involved.
 *
 * @param machineId — identifies which Connector machine holds the file.
 *   Mycelium uses this to look up the correct tunnel URL when the user has
 *   multiple machines.
 */
export function requestRemoteFile(
  filePath: string,
  userId: string,
  ws: WebSocket | null,
  machineId?: string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      reject(new Error('No BATON connection'));
      return;
    }

    const requestId = `rlf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    const timer = setTimeout(() => {
      pendingFileRequests.delete(requestId);
      reject(new Error('Home library unavailable — no response within 5 seconds'));
    }, 5_000);

    pendingFileRequests.set(requestId, {
      resolve,
      reject,
      timer,
    });

    const msg = createCommand('library', {
      cmd: 'requestLocalFile',
      requestId,
      filePath,
      userId,
      machineId,
    } as RequestLocalFileCommand);
    ws.send(JSON.stringify(msg));
  });
}

/**
 * Called when a localFileReady or localFileError event arrives via BATON.
 *
 * Handles two patterns:
 * 1. Dispatch pattern (fire-and-forget): calls playLocalStream() directly
 * 2. Promise pattern (legacy): resolves/rejects the pending Promise
 *
 * iOS PARITY: On iOS, this resolves the tunnel URL that AVPlayer will stream from.
 * The event arrives via the same BATON WebSocket used by all platforms.
 */
export function handleRemoteFileEvent(event: LocalFileReadyEvent | LocalFileErrorEvent): void {
  const requestId = event.requestId;

  // --- Dispatch pattern (non-blocking) ---
  const dispatch = pendingLocalDispatches.get(requestId);
  if (dispatch) {
    pendingLocalDispatches.delete(requestId);
    clearTimeout(dispatch.timer);

    if (event.event === 'localFileReady') {
      const url = (event as LocalFileReadyEvent).url;
      if (dispatch.track?.file_path) cacheFileUrl(dispatch.track.file_path, url);
      import('../handlers/ClientTransportHandler').then(({ getClientTransport }) => {
        const s = useMaestroStore.getState();
        if (s.currentTrack?.id === dispatch.trackId || s.currentTrack?.file_path === dispatch.track?.file_path) {
          const fullTrack = { ...dispatch.track, streamUrl: url };
          getClientTransport().playLocalStream(fullTrack);
        }
        prefetchNextLocalTracks(getClientTransport().getWebSocket(), dispatch.track?.machine_id);
      });
    } else {
      const errorMsg = (event as LocalFileErrorEvent).error;
      console.error('[LocalFile] Error from daemon:', errorMsg);
      const s = useMaestroStore.getState();
      if (s.currentTrack?.id === dispatch.trackId || s.currentTrack?.file_path === dispatch.track?.file_path) {
        s.setPlaybackState('stopped');
      }
    }
    return;
  }

  // --- Promise pattern (used by FullAlbumOverlay, auto-advance) ---
  const pending = pendingFileRequests.get(requestId);
  if (!pending) return;

  pendingFileRequests.delete(requestId);
  clearTimeout(pending.timer);

  if (event.event === 'localFileReady') {
    pending.resolve((event as LocalFileReadyEvent).url);
  } else {
    pending.reject(new Error((event as LocalFileErrorEvent).error));
  }
}

/**
 * Called on the HOME CONNECTOR client when a requestLocalFile command arrives.
 * Registers the file with the local proxy and sends back a localFileReady event.
 *
 * iOS PARITY: This function ONLY runs on the home Connector (Tauri desktop).
 * iOS never executes this — it is always the requesting side, never the serving side.
 * The guard `Connector.isConnectorAvailable()` ensures no Tauri IPC on iOS/web.
 */
export async function handleIncomingFileRequest(
  payload: { requestId: string; filePath: string; userId: string; machineId?: string },
  ws: WebSocket | null,
): Promise<void> {
  if (!Connector.isConnectorAvailable()) return;
  if (!ws || ws.readyState !== WebSocket.OPEN) return;

  try {
    const url = await Connector.registerFileUrl(payload.filePath);
    const expiresAt = Date.now() + 5 * 60 * 1000;
    const msg = createEvent('library', {
      event: 'localFileReady',
      requestId: payload.requestId,
      url,
      expiresAt,
    } as LocalFileReadyEvent);
    ws.send(JSON.stringify(msg));
  } catch (err: any) {
    const msg = createEvent('library', {
      event: 'localFileError',
      requestId: payload.requestId,
      error: err?.message || 'Failed to register file',
    } as LocalFileErrorEvent);
    ws.send(JSON.stringify(msg));
  }
}
